import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ClientSessionService } from '@brighthub/uisession';
import { FINANCIAL_SNAPSHOT_API } from './financial-snapshot-api.token';
import type {
  FinancialSnapshotPostBody,
  FinancialSnapshotPatchBody,
  FinancialSnapshotResponse,
  FinancialSnapshotTemplate,
  SnapshotStatus,
} from './financial-snapshot.model';
import {
  countEnteredUiRows,
  FINANCIAL_SNAPSHOT_UI_ROW_COUNT,
  listIncomeSubItems,
  listSpendingMixRows,
  listUiProgressRows,
} from './mock/financial-snapshot-compute';
import {
  MockFinancialSnapshotApiService,
  type MockSnapshotSeed,
} from './mock/mock-financial-snapshot-api.service';

@Injectable()
export class FinancialSnapshotStore {
  private readonly _api = inject(FINANCIAL_SNAPSHOT_API);
  private readonly _session = inject(ClientSessionService);
  private readonly _mockApi = inject(MockFinancialSnapshotApiService, { optional: true });

  private readonly _template = signal<FinancialSnapshotTemplate | null>(null);
  private readonly _snapshot = signal<FinancialSnapshotResponse | null>(null);
  private readonly _isEmpty = signal(false);
  private readonly _loading = signal(false);
  private readonly _error = signal<unknown | null>(null);
  private readonly _activeGuid = signal<string | null>(null);

  readonly template = this._template.asReadonly();
  readonly snapshot = this._snapshot.asReadonly();
  readonly isEmpty = this._isEmpty.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly activeGuid = this._activeGuid.asReadonly();

  readonly status = computed<SnapshotStatus | null>(() => {
    if (this._isEmpty()) {
      return 'empty';
    }
    return this._snapshot()?.status ?? null;
  });

  readonly enteredRowCount = computed(() =>
    countEnteredUiRows(this._snapshot(), this._template() ?? {})
  );

  readonly progressRows = computed(() =>
    listUiProgressRows(this._snapshot(), this._template() ?? {})
  );

  readonly spendingMixRows = computed(() =>
    listSpendingMixRows(this._snapshot(), this._template() ?? {})
  );

  readonly incomeSubItems = computed(() =>
    listIncomeSubItems(this._snapshot(), this._template() ?? {})
  );

  readonly progressPercent = computed(() =>
    Math.round((this.enteredRowCount() / FINANCIAL_SNAPSHOT_UI_ROW_COUNT) * 100)
  );

  readonly totalUiRows = FINANCIAL_SNAPSHOT_UI_ROW_COUNT;

  readonly sessionGuid = computed(
    () => this._session.selectedSubjectData()?.guid?.trim() || null
  );

  /** True when the mock API is active (dev state switcher). */
  readonly mockAvailable = !!this._mockApi;

  constructor() {
    effect(() => {
      const guid = this.sessionGuid();
      if (guid) {
        void this.load(guid);
      } else {
        this._reset();
      }
    });
  }

  async load(guid: string): Promise<void> {
    this._activeGuid.set(guid);
    this._loading.set(true);
    this._error.set(null);
    try {
      if (!this._template()) {
        const template = await firstValueFrom(this._api.getTemplate());
        this._template.set(template);
      }
      const result = await firstValueFrom(this._api.getSnapshot(guid));
      if (this._activeGuid() !== guid) {
        return;
      }
      if (result.kind === 'empty') {
        this._isEmpty.set(true);
        this._snapshot.set(null);
      } else {
        this._isEmpty.set(false);
        this._snapshot.set(result.snapshot);
      }
    } catch (err) {
      if (this._activeGuid() !== guid) {
        return;
      }
      this._error.set(err);
      this._snapshot.set(null);
      this._isEmpty.set(false);
    } finally {
      if (this._activeGuid() === guid) {
        this._loading.set(false);
      }
    }
  }

  async reload(): Promise<void> {
    const guid = this._activeGuid();
    if (guid) {
      await this.load(guid);
    }
  }

  /** Dev-only: seed mock fixtures for the active client and reload. */
  async seedDevState(seed: MockSnapshotSeed): Promise<void> {
    const guid = this.sessionGuid();
    if (!guid || !this._mockApi) {
      return;
    }
    this._mockApi.seed(guid, seed);
    await this.load(guid);
  }

  async savePost(body: FinancialSnapshotPostBody): Promise<FinancialSnapshotResponse> {
    const guid = body.guid;
    const res = await firstValueFrom(this._api.postSnapshot(body));
    if (this._activeGuid() !== guid) {
      return res;
    }
    this._isEmpty.set(false);
    this._snapshot.set(res);
    return res;
  }

  async savePatch(guid: string, body: FinancialSnapshotPatchBody): Promise<FinancialSnapshotResponse> {
    const res = await firstValueFrom(this._api.patchCategory(guid, body));
    if (this._activeGuid() !== guid) {
      return res;
    }
    this._isEmpty.set(false);
    this._snapshot.set(res);
    return res;
  }

  private _reset(): void {
    this._activeGuid.set(null);
    this._snapshot.set(null);
    this._isEmpty.set(false);
    this._loading.set(false);
    this._error.set(null);
  }
}
